import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { syncNow } from '@/lib/sync';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { num } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/Field';

/**
 * Reset the system: every record removed, the people kept.
 *
 * "Clear demo data" removes demo records only and keeps anything real.
 * This is the other tool — before go-live, nothing from the trial period
 * should remain, whoever captured it. The database does the work in one
 * statement (system_reset, migration 0033); this screen removes the
 * photograph files first, because the database cannot reach the storage
 * bucket, then asks for the word RESET.
 */

type Counts = Record<string, number>;

const REMOVED: [string, string][] = [
  ['machines', 'Machines in the fleet register, with their components'],
  ['daily_diary', 'Daily diaries, with their hours and activities'],
  ['weighbridge_tickets', 'Weighbridge tickets'],
  ['blasts', 'Blasts and survey adjustments'],
  ['breakdowns', 'Breakdowns and the parts they wait for'],
  ['weather_events', 'Weather downtime'],
  ['service_records', 'Services'],
  ['liner_installations', 'Liner installations'],
  ['parts', 'Parts catalogue'],
  ['part_stock_movements', 'Stock movements'],
  ['purchase_orders', 'Purchase orders'],
  ['production_targets', 'Production targets'],
  ['operators', 'Operators'],
  ['suppliers', 'Suppliers'],
  ['attachments', 'Photographs and documents'],
  ['concrete_deliveries', 'Concrete deliveries'],
  ['concrete_targets', 'Concrete targets'],
  ['concrete_customers', 'Concrete customers'],
  ['vehicle_checks', 'Daily vehicle checks'],
  ['machine_transfers', 'Vehicle moves between sites'],
  ['job_cards', 'Workshop job cards, with their hours and parts'],
  ['technicians', 'Technicians'],
  ['audit_log', 'Audit trail entries'],
];

const KEPT = [
  'Users, their sign-ins and roles',
  'Settings, the mail server and report recipients',
  'Machine types, time categories, activity types and shifts',
  'Materials, products and locations',
  'Breakdown categories and weather types',
  'Concrete mixes and disposal reasons, and which branch each user works at',
  'The vehicle checklist',
];

export function ResetPage() {
  const isAdmin = useAuthStore((s) => s.hasRole('admin'));
  const [counts, setCounts] = useState<Counts | null>(null);
  const [word, setWord] = useState('');
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    const { data, error: err } = await supabase.rpc('system_reset_preview');
    if (err) { setError(err.message); return; }
    setCounts((data ?? {}) as Counts);
  }, []);

  useEffect(() => { if (isAdmin) void refresh(); }, [isAdmin, refresh]);

  async function reset() {
    setError(null);
    try {
      // 1. The files. The database removes the records; the bytes live in
      //    the storage bucket, which only the storage API can empty.
      setStep('Removing photographs and documents');
      const { data: files, error: listErr } = await supabase.from('attachments').select('storage_path');
      if (listErr) throw listErr;
      const paths = (files ?? []).map((f) => String(f.storage_path)).filter(Boolean);
      for (let i = 0; i < paths.length; i += 100) {
        // A file already gone is not a reason to stop the reset.
        await supabase.storage.from('evidence').remove(paths.slice(i, i + 100));
      }

      // 2. Everything else, in one statement.
      setStep('Emptying the database');
      const { data, error: resetErr } = await supabase.rpc('system_reset', { p_confirm: word.trim() });
      if (resetErr) throw resetErr;
      const removed = (data as { removed?: Counts })?.removed ?? {};
      const total = Object.values(removed).reduce((s, n) => s + Number(n), 0);

      setStep('Rebuilding this device');
      await syncNow();
      setDone(total);
      setWord('');
      toast.success(`System reset — ${num(total)} records removed`);
      await refresh();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'The reset did not complete');
      toast.error('The reset did not complete');
      await refresh();
    } finally {
      setStep(null);
    }
  }

  if (!isAdmin) {
    return (
      <Panel title="Reset system">
        <p className="muted">Only an administrator can reset the system.</p>
      </Panel>
    );
  }

  const busy = step !== null;
  const total = counts
    ? REMOVED.reduce((s, [k]) => s + Number(counts[k] ?? 0), 0)
    : 0;

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <Panel title="Reset the system">
        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          <div className="issue issue--block" style={{ display: 'block' }}>
            <strong>This removes every record in the system and cannot be undone.</strong> Use it once,
            before go-live, to start clean after testing. Users keep their sign-ins and roles.
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            To remove only the demo data and keep what was captured for real, use{' '}
            <Link to="/admin/demo-data">Demo data → Clear demo data</Link> instead.
          </p>

          {done !== null && (
            <div className="issue issue--ok" style={{ display: 'block' }}>
              The system is clean — {num(done)} records removed. Every tablet rebuilds its copy at its next
              sync. Next: add the fleet, operators, parts and targets —{' '}
              <Link to="/help?guide=administration#setting-up-the-system">Setting up the system</Link>.
            </div>
          )}

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--sp-4)' }}>
            <div>
              <div className="field__label" style={{ marginBottom: 'var(--sp-2)' }}>Removed</div>
              {!counts ? <span className="spinner" /> : (
                <table className="table">
                  <tbody>
                    {REMOVED.map(([k, label]) => (
                      <tr key={k}>
                        <td>{label}</td>
                        <td className="tabular" style={{ textAlign: 'right' }}>{num(counts[k] ?? 0)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td><strong>Total</strong></td>
                      <td className="tabular" style={{ textAlign: 'right' }}><strong>{num(total)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <div className="field__label" style={{ marginBottom: 'var(--sp-2)' }}>Kept</div>
              <ul className="small" style={{ margin: 0, paddingLeft: '1.2em', lineHeight: 1.8 }}>
                {KEPT.map((k) => <li key={k}>{k}</li>)}
                {counts && <li>{num(counts.users ?? 0)} user{counts.users === 1 ? '' : 's'}</li>}
              </ul>
              <p className="small muted" style={{ marginTop: 'var(--sp-3)' }}>
                Numbering starts again (BD-000001, BL-0001). The audit trail is emptied too; its first
                entry records this reset.
              </p>
            </div>
          </div>

          <div className="row" style={{ gap: 'var(--sp-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ width: 240 }}>
              <TextInput
                id="reset-word"
                label="Type RESET to confirm"
                value={word}
                disabled={busy}
                autoComplete="off"
                onChange={setWord}
              />
            </div>
            <Button
              variant="danger"
              loading={busy}
              disabled={busy || word.trim() !== 'RESET'}
              onClick={() => void reset()}
            >
              Reset the system
            </Button>
          </div>

          {step && <div className="small" aria-live="polite">{step}…</div>}
          {error && <div className="issue issue--block" style={{ display: 'block' }}>{error}</div>}
        </div>
      </Panel>
    </div>
  );
}
